import { Component, Inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomFieldDto } from 'src/app/data-transfer/entities/custom-field-dto';
import { CustomFieldAssignmentsProfileDto, DocProfileDto } from 'src/app/data-transfer/entities/doc-entities/doc-profile-dto';
import { EvidenceTypeDto } from 'src/app/data-transfer/core-data/doc/evidence-type-dto';
import { DialogActions } from 'src/app/model/dictionary';

export class DocProfileDialogData {
  constructor(
    public readonly profile: DocProfileDto,
    public readonly isEditingNew: boolean,
    public readonly existingProfileNames: string[],
    public readonly evidenceTypes: EvidenceTypeDto[],
    public readonly availableSystemCustomFields: CustomFieldDto[],
    public readonly availableUnitCustomFields: CustomFieldDto[]
  ) {}
}

interface MergedCustomField {
  name: string;
  systemId?: number;
  unitId?: number;
}

@Component({
  selector: 'app-doc-profile-dialog',
  templateUrl: './doc-profile-dialog.component.html',
  styleUrls: ['./doc-profile-dialog.component.scss'],
})
export class DocProfileDialogComponent {
  readonly sections: readonly number[] = [1, 8];

  nameControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  searchControl = new FormControl('', { nonNullable: true });
  evidenceTypeControl = new FormControl<string[]>([], { nonNullable: true });
  selectedSystemFieldIds: Record<number, number[]> = { 1: [], 8: [] };
  selectedUnitFieldIds: Record<number, number[]> = { 1: [], 8: [] };

  readonly mergedFields: MergedCustomField[];

  private readonly expandedSections = new Set<number>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DocProfileDialogData,
    private dialogRef: MatDialogRef<DocProfileDialogComponent>
  ) {
    this.nameControl.setValue(data.profile.profileName);
    this.evidenceTypeControl.setValue([...data.profile.excludedEvidenceTypes]);

    for (const section of this.sections) {
      const selectedIds = this.decodeSelectedIds(section === 1 ? data.profile.customFieldsForSection1 : data.profile.customFieldsForSection8);
      this.selectedSystemFieldIds[section] = data.availableSystemCustomFields.filter(field => selectedIds.has(field.id)).map(field => field.id);
      this.selectedUnitFieldIds[section] = data.availableUnitCustomFields.filter(field => selectedIds.has(field.id)).map(field => field.id);
    }

    const byName = new Map<string, MergedCustomField>();
    for (const field of data.availableSystemCustomFields) {
      byName.set(field.name, { ...(byName.get(field.name) ?? { name: field.name }), systemId: field.id });
    }
    for (const field of data.availableUnitCustomFields) {
      byName.set(field.name, { ...(byName.get(field.name) ?? { name: field.name }), unitId: field.id });
    }
    this.mergedFields = [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  private decodeSelectedIds(assignments: CustomFieldAssignmentsProfileDto | null): Set<number> {
    return new Set((assignments?.fields ?? []).filter(field => field.includeField).map(field => field.customFieldId));
  }

  private buildSectionAssignments(section: number): CustomFieldAssignmentsProfileDto {
    const selectedIds = new Set([...this.selectedSystemFieldIds[section], ...this.selectedUnitFieldIds[section]]);
    return { fields: [...selectedIds].map(customFieldId => ({ customFieldId, includeField: true })) };
  }

  isFieldSelected(section: number, field: MergedCustomField): boolean {
    return (field.systemId != null && this.selectedSystemFieldIds[section].includes(field.systemId))
      || (field.unitId != null && this.selectedUnitFieldIds[section].includes(field.unitId));
  }

  toggleField(section: number, field: MergedCustomField): void {
    const include = !this.isFieldSelected(section, field);
    if (field.systemId != null) { this.setId(this.selectedSystemFieldIds[section], field.systemId, include); }
    if (field.unitId != null) { this.setId(this.selectedUnitFieldIds[section], field.unitId, include); }
  }

  private setId(ids: number[], id: number, include: boolean): void {
    const index = ids.indexOf(id);
    if (include && index === -1) {
      ids.push(id);
    } else if (!include && index !== -1) {
      ids.splice(index, 1);
    }
  }

  isSectionExpanded(section: number): boolean {
    return !!this.searchControl.value.trim() || this.expandedSections.has(section);
  }

  onSectionExpandedChange(section: number, expanded: boolean): void {
    if (expanded) {
      this.expandedSections.add(section);
    } else {
      this.expandedSections.delete(section);
    }
  }

  filteredFields(): MergedCustomField[] {
    const query = this.searchControl.value.trim().toLowerCase();
    if (!query) return this.mergedFields;
    return this.mergedFields.filter(field => field.name.toLowerCase().includes(query));
  }

  areAllFieldsSelected(section: number): boolean {
    const fields = this.filteredFields();
    return fields.length > 0 && fields.every(field => this.isFieldSelected(section, field));
  }

  toggleAllFields(section: number): void {
    const selectAll = !this.areAllFieldsSelected(section);
    for (const field of this.filteredFields()) {
      if (field.systemId != null) { this.setId(this.selectedSystemFieldIds[section], field.systemId, selectAll); }
      if (field.unitId != null) { this.setId(this.selectedUnitFieldIds[section], field.unitId, selectAll); }
    }
  }

  save(): void {
    this.nameControl.markAsTouched();
    const trimmedName = this.nameControl.value.trim();
    if (!trimmedName) {
      this.nameControl.setErrors({ required: true });
      return;
    }
    if (this.data.existingProfileNames.includes(trimmedName)) {
      this.nameControl.setErrors({ duplicateName: true });
      return;
    }

    const savedProfile: DocProfileDto = {
      ...this.data.profile,
      profileName: trimmedName,
      customFieldsForSection1: this.buildSectionAssignments(1),
      customFieldsForSection8: this.buildSectionAssignments(8),
      excludedEvidenceTypes: this.evidenceTypeControl.value,
    };
    this.dialogRef.close({ event: DialogActions.CONFIRM, data: savedProfile });
  }

  closeDialog(): void {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }
}
